import React, { useState, useEffect, useCallback } from 'react';
import { getProjects, getScheduleLogPMs, getPMScheduleSummary } from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';
import SegmentedControl from '../components/ui/SegmentedControl';
import ScheduleLogTab from '../components/ScheduleLogTab';
import {
  SlIcon, fmtTime, FieldChips, Timeline, SlippageTable, CountChip, Card, CardTitle, EmptyInline, TokenSelect,
} from '../components/schedulelog/ScheduleLogShared';

/* ═══════════════════════════════════════════════════════════════════════════
   Schedule Change Log page (/schedule-log) — warm-cream + sage redesign
   Page shell (mode switcher) + By-PM overview; By-Project mode embeds the
   redesigned ScheduleLogTab. Token-only colors. Visual reskin — API + state
   logic unchanged.
   ═══════════════════════════════════════════════════════════════════════════ */

const ring = (c) => `color-mix(in oklch, ${c} 22%, transparent)`;
const soft = (c) => `color-mix(in oklch, ${c} 14%, var(--bg-surface))`;

/* ─────────────── PM overview ─────────────── */

const KpiTile = ({ label, value, sub, tone, muted }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 150px', minWidth: 140, padding: '15px 18px', borderRadius: 10, background: muted ? 'transparent' : soft(tone), border: `1px solid ${muted ? 'transparent' : ring(tone)}` }}>
    <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</div>
    <div className="mono" style={{ fontSize: 29.5, fontWeight: 800, lineHeight: 1, color: muted ? 'var(--text-4)' : tone, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {sub && <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.3 }}>{sub}</div>}
  </div>
);
const KpiDivider = () => <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)', margin: '10px 0', flex: '0 0 auto' }} />;

const volTone = (n) => (n <= 10 ? 'var(--success)' : n <= 20 ? 'var(--warning)' : 'var(--danger)');
const ChangeCountBadge = ({ n }) => {
  const tone = volTone(n);
  return (
    <span className="mono" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 38, height: 22, padding: '0 9px', borderRadius: 999, fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: tone, background: soft(tone), border: `1px solid ${ring(tone)}` }}>{n}</span>
  );
};

const Th = ({ children, align = 'left', w }) => (
  <th style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-surface-2)', textAlign: align, width: w, padding: '10px 14px', borderBottom: '1px solid var(--border-default)', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{children}</th>
);

const ByProjectTable = ({ rows, onOpenProject }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', minWidth: 820, borderCollapse: 'separate', borderSpacing: 0 }}>
      <thead>
        <tr>
          <Th w={132}>Number</Th>
          <Th>Project</Th>
          <Th w={104} align="center">Changes</Th>
          <Th w={180} align="right">First</Th>
          <Th w={180} align="right">Latest</Th>
          <Th w={76} align="center">Open</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.project_id} className="sl-prow" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <td className="mono" style={{ padding: '11px 14px', fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.project_number || '—'}</td>
            <td style={{ padding: '11px 14px', fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>{r.project_name}</td>
            <td style={{ padding: '11px 14px', textAlign: 'center' }}><ChangeCountBadge n={r.change_count} /></td>
            <td className="mono" style={{ padding: '11px 14px', textAlign: 'right', fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtTime(r.first_change)}</td>
            <td className="mono" style={{ padding: '11px 14px', textAlign: 'right', fontSize: 12.5, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtTime(r.last_change)}</td>
            <td style={{ padding: '11px 14px', textAlign: 'center' }}>
              <button type="button" className="sl-openbtn" onClick={() => onOpenProject(r.project_id)} aria-label={`Open ${r.project_name}`} style={{ width: 28, height: 28, borderRadius: 7, color: 'var(--text-3)', border: '1px solid var(--border-default)', background: 'var(--bg-surface)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <SlIcon name="arrow-up-r" size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PM_VIEWS = [
  { value: 'projects', label: 'By Project' },
  { value: 'recent', label: 'Recent Activity' },
  { value: 'slippage', label: 'Slippage' },
];

const PMOverview = ({ pmData, onOpenProject }) => {
  const [view, setView] = useState('projects');
  const projects = pmData.projects || [];
  const byField = pmData.byField || [];
  const byProject = pmData.byProject || [];
  const recentChanges = pmData.recentChanges || [];
  const slippage = pmData.slippage || [];
  const totalChanges = pmData.totalChanges || 0;
  const k = {
    projects: projects.length,
    totalChanges,
    finishSlips: slippage.length,
    avgPerProject: projects.length > 0 ? Math.round(totalChanges / projects.length) : 0,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', padding: 6, gap: 0, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
        <KpiTile label="Projects" value={k.projects} tone="var(--accent-text)" muted={k.projects === 0} />
        <KpiDivider />
        <KpiTile label="Total Changes" value={k.totalChanges} tone="var(--text-1)" muted={k.totalChanges === 0} />
        <KpiDivider />
        <KpiTile label="Finish Slips" value={k.finishSlips} sub={k.finishSlips > 0 ? 'dates pushed out' : 'none recorded'} tone={k.finishSlips > 0 ? 'var(--danger)' : 'var(--success)'} muted={false} />
        <KpiDivider />
        <KpiTile label="Avg / Project" value={k.avgPerProject} sub="changes" tone="var(--warning)" muted={k.totalChanges === 0} />
      </section>

      {byField.length > 0 && <FieldChips byField={byField} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <SegmentedControl value={view} onChange={setView} options={PM_VIEWS} />
        <div style={{ flex: 1 }} />
      </div>

      {totalChanges === 0 ? (
        <Card>
          <EmptyInline title="No schedule changes recorded" body={`This person has ${k.projects} project${k.projects === 1 ? '' : 's'} but no schedule changes have been logged yet.`} />
        </Card>
      ) : view === 'projects' ? (
        <Card pad={false} header={<><CardTitle>Change volume by project</CardTitle><div style={{ flex: 1 }} /><CountChip>{byProject.length} projects</CountChip></>}>
          <ByProjectTable rows={byProject} onOpenProject={onOpenProject} />
        </Card>
      ) : view === 'recent' ? (
        <Card header={<><CardTitle>Recent activity</CardTitle><div style={{ flex: 1 }} /><CountChip>{recentChanges.length} most recent</CountChip></>}>
          <Timeline events={recentChanges} showProject />
        </Card>
      ) : (
        <Card pad={false} header={<><CardTitle>Finish-date slippage</CardTitle><div style={{ flex: 1 }} /><CountChip>{slippage.length} slips</CountChip></>}>
          {slippage.length === 0 ? (
            <EmptyInline title="No finish date slippage recorded" body="None of this team member's tasks have had their finish dates pushed out." />
          ) : (
            <SlippageTable rows={slippage} showProject />
          )}
        </Card>
      )}
    </div>
  );
};

/* ─────────────── page ─────────────── */

const MODE_OPTIONS = [
  { value: 'pm', label: 'By PM' },
  { value: 'project', label: 'By Project' },
];

const ScheduleLogPage = ({ onOpenProject }) => {
  const [projects, setProjects] = useState([]);
  const [pms, setPMs] = useState([]);
  const [selectedPM, setSelectedPM] = useState('');
  const [pmData, setPmData] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loading, setLoading] = useState(true);
  const [pmLoading, setPmLoading] = useState(false);
  const [mode, setMode] = useState('pm');

  useEffect(() => {
    (async () => {
      try {
        const [projectData, pmList] = await Promise.all([getProjects(), getScheduleLogPMs()]);
        setProjects(Array.isArray(projectData) ? projectData : []);
        setPMs(Array.isArray(pmList) ? pmList : []);
        if (Array.isArray(projectData) && projectData.length > 0) setSelectedProjectId(String(projectData[0].id));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handlePMSelect = useCallback(async (pmName) => {
    setSelectedPM(pmName);
    if (!pmName) { setPmData(null); return; }
    try {
      setPmLoading(true);
      const data = await getPMScheduleSummary(pmName);
      setPmData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setPmLoading(false);
    }
  }, []);

  if (loading) return <LoadingState message="Loading schedule log…" padding="80px 20px" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        @keyframes slSpin { to { transform: rotate(360deg); } }
        .sl-ghost:hover, .sl-openbtn:hover { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); color: var(--accent-text); background: var(--accent-soft); }
        .sl-fieldchip:hover { border-color: var(--border-strong); background: var(--bg-hover); }
        .sl-prow:hover { background: var(--bg-hover); }
        .sl-prow:hover td:nth-child(2) { color: var(--accent-text); }
        .sl-select:focus-visible { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
      `}</style>

      {/* mode switcher */}
      <section style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '10px 14px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
        <SegmentedControl value={mode} onChange={setMode} options={MODE_OPTIONS} />
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)', margin: '2px 2px', flex: '0 0 auto' }} />
        {mode === 'pm' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-3)', letterSpacing: '0.01em' }}>Person</span>
            <TokenSelect value={selectedPM} onChange={handlePMSelect} placeholder="Select a team member…" minWidth={240} options={pms.map((name) => ({ value: name, label: name }))} />
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-3)', letterSpacing: '0.01em' }}>Project</span>
            <TokenSelect
              value={selectedProjectId}
              onChange={setSelectedProjectId}
              placeholder="Select a project…"
              minWidth={300}
              options={projects.map((p) => ({ value: String(p.id), label: `${p.project_name}${p.project_number ? `  ·  ${p.project_number}` : ''}` }))}
            />
            {selectedProjectId && (
              <button type="button" className="sl-ghost" onClick={() => onOpenProject(selectedProjectId)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 13px', borderRadius: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer' }}>
                <SlIcon name="arrow-up-r" size={14} /> Open Project
              </button>
            )}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 8 }} />
      </section>

      {mode === 'pm' ? (
        !selectedPM ? (
          <StateScreen kind="empty" title="Select a team member" body="Choose a person above to see their schedule change history across every project they manage." />
        ) : pmLoading ? (
          <LoadingState message="Loading PM data…" padding="60px 20px" />
        ) : pmData ? (
          <PMOverview pmData={pmData} onOpenProject={onOpenProject} />
        ) : null
      ) : !selectedProjectId ? (
        <StateScreen kind="empty" title="Select a project" body="Choose a project above to see its full schedule change log — every task date, duration, and progress edit." />
      ) : (
        <ScheduleLogTab projectId={selectedProjectId} />
      )}
    </div>
  );
};

export default ScheduleLogPage;
